import React, { useEffect, useMemo, useId } from "react";
import { Helmet } from "react-helmet-async";
import ProfileHeader from "./ProfileHeader";
import AssessmentCard from "../incbotFeedback/AssessmentCard";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getFeedback } from "../../../redux/action";
import { AppDispatch } from "../../../redux/store";
import {
  getFeedbackForm,
  getInterviewResult,
  getLoading,
  getProfileForm,
} from "../../../redux/reducer/incbotFeedbackReducer";
import skeletonLoad from "../../../assets/gifs/skeleton-load.gif";
import { Loader } from "../../ui/loader";
import { capitalizeAll, getStatusV } from "../../../utils/helper";
// [2026-10-08] ADDED — which Activity Log entries the "Call Events Log" card shows (now with scheduling).
import { callEventsForReport } from "../../../utils/callEventsLog";
import NotFound from "../notFound/NotFound";
import { Avatar, AvatarFallback } from "../../ui/avatar";
import { Card, CardContent } from "../../ui/card";
import {
  TrendingUp,
  Building2,
  DollarSign,
  MapPin,
  Target,
  Star,
  FileText,
  CheckCircle,
  CircleX,
  MessageSquare,
  LayoutDashboard,
  LayoutPanelLeft,
} from "lucide-react";
import infoBulb from "../../../assets/images/commnucation-assessment-bulb.svg";
import callEventsLog from "../../../assets/images/external-devices/callEventsLog.svg";
import FollowUpConversation from "./FollowUpConversation";

import { Tooltip } from "react-tooltip";

// Types
interface QuestionItem {
  question: string;
  answer: string;
}

const cardStyles: Record<"green" | "blue" | "red" | "orange", string> = {
  green: "border-[#BBF7D0] bg-[linear-gradient(135deg,_#F0FDF4_0%,_#ECFDF5_100%)] text-[#15803D]",
  blue: "border-[#BFDBFE] bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] text-[#2563EB]",
  red: "border-[#FECACA] bg-[#FEF2F2] text-[#DC2626]",
  orange: "border-[#FFCBB6] bg-[#FFF6F2] text-[#EC4C0A]",
};

const iconStyles: Record<"green" | "blue" | "red" | "orange", string> = {
  green: "text-success",
  blue: "text-info",
  red: "text-destructive",
  orange: "text-warning",
};

// Reusable Components
const ConversationItem: React.FC<QuestionItem & { isLast?: boolean; name?: string }> = ({
  question,
  answer,
  isLast = false,
  name = "",
}) => (
  <div className={`space-y-4 ${!isLast ? "pb-6 border-b border-border" : ""}`}>
    {question && (
      <div className="flex gap-3">
        <Avatar className="h-8 w-8 bg-gradient-to-br from-teal-500 to-blue-600 text-white">
          <AvatarFallback className="text-xs font-semibold">IB</AvatarFallback>
        </Avatar>
        <div className="flex-1 bg-blue-50 rounded-2xl p-4">
          <div className="text-sm font-medium text-foreground mb-1">IncScreen</div>
          <div className="text-sm text-muted-foreground leading-relaxed">{question}</div>
        </div>
      </div>
    )}
    {answer && (
      <div className="flex gap-3">
        <div className="flex-1 bg-[#F3F4F6] rounded-2xl p-4 ml-11">
          <div className="text-sm font-medium text-foreground mb-1">{name}</div>
          <div className="text-sm text-muted-foreground leading-relaxed">{answer}</div>
        </div>
        <Avatar className="h-8 w-8 bg-blue-600 text-white">
          <AvatarFallback className="text-xs font-semibold">
            {name
              .split(" ")
              .map((n: any) => n[0])
              .join("")}
          </AvatarFallback>
        </Avatar>
      </div>
    )}
  </div>
);

type Category = "green" | "blue" | "red" | "orange";

const MetricCard: React.FC<{
  title: string;
  value: string;
  benchmark: string;
  icon: React.ReactNode;
  category: Category;
}> = ({ title, value, benchmark, icon, category }) => {
  const showValueTooltip = value?.length > 18;
  const displayValue = showValueTooltip ? `${value.slice(0, 18)}...` : value;
  const valueTooltipId = useId();

  return (
    <Card
      className={`${cardStyles[category]} border`}
      // style={{ height: "122px", width: "283.5px" }}
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium">{title}</span>
          <div className={`p-2 ${iconStyles[category]}`}>{icon}</div>
        </div>
        <div className="space-y-1">
          <div
            className="text-2xl font-bold"
            data-tooltip-id={showValueTooltip ? valueTooltipId : undefined}
            data-tooltip-content={showValueTooltip ? value : undefined}
          >
            {displayValue}
          </div>
          {showValueTooltip && (
            <Tooltip
              id={valueTooltipId}
              place="top"
              style={{
                maxWidth: "260px",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            />
          )}
          <div className="text-xs">{benchmark}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const SectionWiseScores: React.FC<{
  data: any;
  evaluationType?: string;
  passThreshold?: number;
  sectionWeights?: { section1: number; section2: number };
}> = ({
  data,
  evaluationType = "qualify_then_score",
  passThreshold = 50,
  sectionWeights = { section1: 40, section2: 60 },
}) => {
  const isCombined = evaluationType === "combined_weightage";

  const section1PassRate = (() => {
    const qs = data?.screeningQuestions?.logisticsQuestions;
    if (!qs?.length) return null;
    const passed = qs.filter((q: any) => q.verdict === "pass").length;
    return (passed / qs.length) * 100;
  })();

  const section2AvgPct = (() => {
    const qs = data?.screeningQuestions?.generalQuestions;
    if (!qs?.length) return null;
    const scores = qs.map((q: any) => q.score || 0);
    return (scores.reduce((a: number, b: number) => a + b, 0) / scores.length) * 10;
  })();

  const weightedFinal =
    isCombined && section1PassRate !== null && section2AvgPct !== null
      ? (section1PassRate * sectionWeights.section1) / 100 +
        (section2AvgPct * sectionWeights.section2) / 100
      : null;

  const barColor = (pct: number) =>
    pct >= 70 ? "bg-green-500" : pct > 40 ? "bg-orange-500" : "bg-red-500";

  return (
    <Card className="border border-gray-200 shadow-sm rounded-[12px] bg-white">
      <CardContent className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2 flex items-center gap-2">
          <LayoutPanelLeft className="h-5 w-5 text-blue-600" />
          Section-wise Scores
          {isCombined && (
            <span className="ml-auto text-xs font-normal text-gray-500 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">
              Combined Weightage
            </span>
          )}
        </h3>
        <hr className="border-b border-[#F3F4F6] my-2" />

        <div className="space-y-4">
          {data?.version <= 2 ? (
            <>
              {data?.screeningQuestions?.generalQuestions?.length > 0 &&
                (() => {
                  const passed = data.screeningQuestions.generalQuestions.filter(
                    (q: any) => q.verdict === "pass"
                  ).length;
                  const total = data.screeningQuestions.generalQuestions.length;
                  return (
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs md:text-sm font-medium text-gray-700">
                          Candidate Profile & Fit Assessment
                        </span>
                        <span className="text-xs md:text-sm text-gray-500">
                          {passed}/{total}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1 md:h-2">
                        <div
                          className="h-1 md:h-2 rounded-full bg-blue-500"
                          style={{ width: `${(passed / total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })()}
              {data?.screeningQuestions?.logisticsQuestions?.length > 0 &&
                (() => {
                  const scores = data.screeningQuestions.logisticsQuestions.map(
                    (q: any) => q.score || 0
                  );
                  const avgPercentage =
                    (scores.reduce((a: number, b: number) => a + b, 0) / scores.length) * 10;
                  const color = barColor(avgPercentage);
                  return (
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs md:text-sm font-medium text-gray-700">
                          Position Specific
                        </span>
                        <span className="text-xs md:text-sm text-gray-500">
                          {avgPercentage.toFixed(0)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1 md:h-2">
                        <div
                          className={`h-1 md:h-2 rounded-full ${color}`}
                          style={{ width: `${avgPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })()}
            </>
          ) : isCombined ? (
            <>
              {section1PassRate !== null && (
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs md:text-sm font-medium text-gray-700">
                      Section 1 — Candidate Profile & Fit
                      <span className="ml-2 text-xs text-gray-400">
                        ({sectionWeights.section1}% weight)
                      </span>
                    </span>
                    <span className="text-xs md:text-sm text-gray-500">
                      {section1PassRate.toFixed(0)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1 md:h-2">
                    <div
                      className={`h-1 md:h-2 rounded-full ${barColor(section1PassRate)}`}
                      style={{ width: `${section1PassRate}%` }}
                    ></div>
                  </div>
                </div>
              )}
              {section2AvgPct !== null && (
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs md:text-sm font-medium text-gray-700">
                      Section 2 — Position Specific
                      <span className="ml-2 text-xs text-gray-400">
                        ({sectionWeights.section2}% weight)
                      </span>
                    </span>
                    <span className="text-xs md:text-sm text-gray-500">
                      {section2AvgPct.toFixed(0)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1 md:h-2">
                    <div
                      className={`h-1 md:h-2 rounded-full ${barColor(section2AvgPct)}`}
                      style={{ width: `${section2AvgPct}%` }}
                    ></div>
                  </div>
                </div>
              )}
              {weightedFinal !== null &&
                (() => {
                  const meetsThreshold = weightedFinal >= passThreshold;
                  return (
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs md:text-sm font-semibold text-gray-800">
                          Weighted Final Score
                          <span className="ml-2 text-xs text-gray-400">
                            (Threshold: {passThreshold}%)
                          </span>
                        </span>
                        <span
                          className={`text-xs md:text-sm font-semibold px-2 py-0.5 rounded-full ${
                            meetsThreshold
                              ? "bg-green-50 text-green-700 border border-green-200"
                              : "bg-red-50 text-red-700 border border-red-200"
                          }`}
                        >
                          {weightedFinal.toFixed(1)}% —{" "}
                          {meetsThreshold ? "Passed" : "Below threshold"}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1 md:h-2">
                        <div
                          className={`h-1 md:h-2 rounded-full ${barColor(weightedFinal)}`}
                          style={{ width: `${Math.min(weightedFinal, 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        A candidate must score at least {passThreshold} / 100 on the final weighted
                        score to pass.
                      </p>
                    </div>
                  );
                })()}
            </>
          ) : (
            <>
              {data?.screeningQuestions?.logisticsQuestions?.length > 0 &&
                (() => {
                  const passed = data.screeningQuestions.logisticsQuestions.filter(
                    (q: any) => q.verdict === "pass"
                  ).length;
                  const total = data.screeningQuestions.logisticsQuestions.length;
                  return (
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs md:text-sm font-medium text-gray-700">
                          Section 1 — Candidate Profile & Fit Assessment
                          <span className="ml-2 text-xs text-gray-400">(Qualifying Round)</span>
                        </span>
                        <span className="text-xs md:text-sm text-gray-500">
                          {passed}/{total} passed
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1 md:h-2">
                        <div
                          className="h-1 md:h-2 rounded-full bg-blue-500"
                          style={{ width: `${(passed / total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })()}
              {data?.screeningQuestions?.generalQuestions?.length > 0 &&
                (() => {
                  const scores = data.screeningQuestions.generalQuestions.map(
                    (q: any) => q.score || 0
                  );
                  const avgPercentage =
                    (scores.reduce((a: number, b: number) => a + b, 0) / scores.length) * 10;
                  const color = barColor(avgPercentage);
                  const meetsThreshold = avgPercentage >= passThreshold;
                  return (
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs md:text-sm font-medium text-gray-700">
                          Section 2 — Position Specific
                          <span className="ml-2 text-xs text-gray-400">
                            (Threshold: {passThreshold}%)
                          </span>
                        </span>
                        <span
                          className={`text-xs md:text-sm font-medium px-2 py-0.5 rounded-full ${
                            meetsThreshold
                              ? "bg-green-50 text-green-700 border border-green-200"
                              : "bg-red-50 text-red-700 border border-red-200"
                          }`}
                        >
                          {avgPercentage.toFixed(0)}% —{" "}
                          {meetsThreshold ? "Passed" : "Below threshold"}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1 md:h-2 relative">
                        <div
                          className={`h-1 md:h-2 rounded-full ${color}`}
                          style={{ width: `${avgPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })()}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Main Component
const Feedback = () => {
  const { roomId = "", interviewId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const signature = searchParams.get("signature") || "";
  const expires = searchParams.get("expires") || "";

  const dispatch = useDispatch<AppDispatch>();
  const profileForm = useSelector(getProfileForm);
  const data = useSelector(getFeedbackForm);
  const interviewResult = useSelector(getInterviewResult);
  const loading = useSelector(getLoading);
  useEffect(() => {
    if (roomId && interviewId) {
      dispatch(getFeedback({ roomId, interviewId, signature, expires }));
    }
  }, [dispatch, roomId, interviewId, signature, expires]);

  // [2026-10-27] CHANGED — the IncScreen mapper keeps the activity log at `data.logs`
  // (incScreenFeedbackData); `data.proctoringLogs` never exists here, so this was always false.
  // Old: data?.proctoringLogs?.logs?.some(...) with deps [data?.proctoringLogs?.logs]
  const hasChatInitiated = useMemo(
    () => data?.logs?.some((log: any) => log.action === "Chat Initiated"),
    [data?.logs]
  );

  useEffect(() => {
    if (profileForm?.name) {
      document.title = `${capitalizeAll(profileForm.name)} Feedback Report`;
    }
  }, [profileForm]);

  const hasStartChat = useMemo(() => {
    const turns = data?.conversationTranscript;
    if (!Array.isArray(turns)) return false;
    return turns.some(
      (entry: { text?: string }) =>
        typeof entry?.text === "string" && entry.text.includes("Start Chat")
    );
  }, [data?.conversationTranscript]);
  // [2026-10-27] CHANGED — WhatsApp interviews were shown as "Call": consent-flow chats open with
  // "Yes, I Confirm", not "Start Chat", and the log check above never matched. `interviewType`
  // (["Call"], ["Whatsapp"] or both) now decides when it names one channel; only a candidate set
  // up for both still falls back to the log / transcript checks.
  // Old: const shouldHide = hasChatInitiated || hasStartChat;
  const interviewTypes = Array.isArray(profileForm?.interviewType) ? profileForm.interviewType : [];
  const hasCall = interviewTypes.includes("Call");
  const hasWhatsapp = interviewTypes.includes("Whatsapp");
  const isSingleChannel = hasCall !== hasWhatsapp;
  const shouldHide = isSingleChannel ? hasWhatsapp : hasChatInitiated || hasStartChat;
  // [2026-10-08] ADDED — INC-1785: a stored Fluency of 0 (or none) means the call's audio could
  // not be scored, never that the candidate scored 0: the scorer's lowest real score is 2.
  // Confidence is 80% Fluency, so it cannot be shown either. Reports saved while the scorer could
  // not open Vapi's recordings all carry this 0.
  const fluencyNotMeasured = !(Number(data?.communicationAssessment?.fluencyRating) > 0);
  return (
    <div className="min-h-screen bg-[#F9FAFB] font-manrope">
      {profileForm?.name && (
        <Helmet>
          <title>{capitalizeAll(profileForm.name)} Feedback Report | InCruiter</title>
          <meta
            name="description"
            content={`Interview feedback report for ${profileForm.name}${profileForm.position ? ` – ${profileForm.position}` : ""}${profileForm.company ? ` at ${profileForm.company}` : ""}`}
          />
          <meta
            property="og:title"
            content={`${capitalizeAll(profileForm.name)} Feedback Report | InCruiter`}
          />
          <meta
            property="og:description"
            content={`Interview feedback report for ${profileForm.name}${profileForm.position ? ` – ${profileForm.position}` : ""}${profileForm.company ? ` at ${profileForm.company}` : ""}`}
          />
          <meta
            name="twitter:title"
            content={`${capitalizeAll(profileForm.name)} Feedback Report | InCruiter`}
          />
          <meta
            name="twitter:description"
            content={`Interview feedback report for ${profileForm.name}${profileForm.position ? ` – ${profileForm.position}` : ""}${profileForm.company ? ` at ${profileForm.company}` : ""}`}
          />
        </Helmet>
      )}
      <div className="flex justify-center bg-white">
        <div className="max-w-6xl w-full bg-gray-50 print-a3-container">
          <ProfileHeader shouldHide={shouldHide} />
        </div>
      </div>

      <div className="flex justify-center bg-[#F9FAFB]">
        <div className="max-w-6xl w-full bg-gray-50 print-a3-container">
          {/* Metrics */}

          {/* Candidate Summary */}
          <div className="bg-white rounded-lg border border-gray-200 my-3">
            <h2 className="text-lg font-semibold text-gray-900 mb-3 md:mb-4 flex items-center gap-2 pl-4 pr-4 pt-4">
              <span
                className="increen-communication-icon p-2 rounded-lg"
                style={{ backgroundColor: "#EEF2FF" }}
              >
                <LayoutDashboard className="h-5 w-5" />
              </span>{" "}
              Candidate Summary
            </h2>
            <hr className="border-b border-[#F3F4F6]" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pl-4 pr-4 pt-4">
              <MetricCard
                title="Availability"
                value={`${data?.availability?.value || "N/A"} Days`}
                benchmark={`Benchmark: ${data?.availability?.benchmark || "N/A"} Days`}
                icon={<TrendingUp className="h-5 w-5" />}
                category={
                  data?.availability?.category === "Reject"
                    ? "red"
                    : data?.availability?.category === "Ideal"
                      ? "green"
                      : "orange"
                }
              />
              <MetricCard
                title="Employment (Current)"
                value={data?.currentEmployment?.value || "N/A"}
                benchmark="Current Company"
                icon={<Building2 className="h-5 w-5" />}
                category="blue"
              />
              <MetricCard
                title="Salary Expectation"
                value={data?.salaryExpectation?.value || "N/A"}
                benchmark={`Benchmark: ${data?.salaryExpectation?.benchmark || "N/A"}`}
                icon={<DollarSign className="h-5 w-5" />}
                category={
                  data?.salaryExpectation?.category === "Reject"
                    ? "red"
                    : data?.salaryExpectation?.category === "Ideal"
                      ? "green"
                      : "orange"
                }
              />
              <MetricCard
                title="Work Setup"
                value={data?.workSetup?.value || "N/A"}
                benchmark={`Benchmark: ${data?.workSetup?.benchmark || "N/A"}`}
                icon={<MapPin className="h-5 w-5" />}
                category={
                  data?.workSetup?.category === "Reject"
                    ? "red"
                    : data?.workSetup?.category === "Ideal"
                      ? "green"
                      : "orange"
                }
              />
            </div>
            <p className="flex items-center text-xs text-gray-600 italic font-medium pl-4 pr-4 pb-4 mt-2"></p>
          </div>

          {/* Communication Assessment */}
          {!shouldHide && (
            <div className="bg-white rounded-lg border border-gray-200 my-3">
              <h2 className="text-lg font-semibold text-gray-900 mb-3 md:mb-4 flex items-center gap-2 pl-4 pr-4 pt-4">
                <span
                  className="increen-communication-icon p-2 rounded-lg"
                  style={{ backgroundColor: "#EEF2FF" }}
                >
                  <MessageSquare className="h-5 w-5" />
                </span>{" "}
                Communication Assessment
              </h2>
              <hr className="border-b border-[#F3F4F6]" />
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pl-4 pr-4 pt-4">
                {[
                  {
                    title: "Fluency",
                    value: data?.communicationAssessment?.fluencyRating ?? 0,
                  },
                  {
                    title: "Clarity",
                    value: data?.communicationAssessment?.clarityRating ?? 0,
                  },
                  {
                    title: "Grammar",
                    value: data?.communicationAssessment?.grammarRating ?? 0,
                  },
                  {
                    title: "Confidence",
                    value: data?.communicationAssessment?.confidenceRating ?? 0,
                  },
                ]
                  .filter((item) => typeof item.value === "number")
                  .map((item, key) => {
                    const score = item.value;
                    const isConfidence = item.title === "Confidence";
                    // [2026-10-08] ADDED — INC-1785
                    const notAvailable =
                      fluencyNotMeasured && (item.title === "Fluency" || isConfidence);
                    const description = isConfidence
                      ? score > 8
                        ? "High"
                        : score > 6
                          ? "Good"
                          : score > 4
                            ? "Moderate"
                            : "Low"
                      : getStatusV(score).title;
                    const color = isConfidence
                      ? ((score > 8 ? "green" : score > 4 ? "blue" : "red") as
                          | "blue"
                          | "red"
                          | "green"
                          | "yellow")
                      : (getStatusV(score).color as "blue" | "red" | "green" | "yellow");
                    return (
                      <AssessmentCard
                        key={key}
                        title={item.title}
                        // [2026-10-08] CHANGED — INC-1785: "N/A – Not available" instead of
                        // "0/10 – Very Poor" when Fluency was not measured. Old lines:
                        // score={score}
                        // description={description}
                        score={notAvailable ? "N/A" : score}
                        description={notAvailable ? "Not available" : description}
                        color={"blue"}
                        notAvailable={notAvailable}
                      />
                    );
                  })}
              </div>
              <p className="flex items-center text-xs text-gray-600 italic font-medium pl-4 pr-4 pb-4 mt-2">
                <span>
                  <img src={infoBulb} alt="info bulb" className="w-4 h-4" />
                </span>{" "}
                Communication skills are best evaluated with responses of 50+ words. Shorter
                responses may have less accuracy.
              </p>
            </div>
          )}

          {/* Remarks Section */}
          <Card className="border border-gray-200 shadow-sm my-3 bg-[#EEF2FF]">
            <div className="text-center pt-3">
              <h2 className="text-xl font-semibold text-cyan-500">Remarks</h2>
              <div className="w-98 h-0.5 bg-cyan-500 mx-auto mt-2" />
            </div>
          </Card>

          {/* Cultural Fit + Deal Breakers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 my-3">
            <Card className="bg-white shadow-sm border border-gray-200">
              <CardContent className="p-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium flex items-center gap-2 text-gray-900">
                    <Target className="h-5 w-5 text-blue-600" />
                    Candidate Profile & Fitment Remarks
                  </h3>
                  <div className="flex items-center gap-2 text-sm rounded-[20px] border border-[#BBF7D0] bg-[linear-gradient(135deg,_#F0FDF4_0%,_#ECFDF5_100%)] text-[#16A34A] px-3 py-1">
                    <Star className="h-5 w-5 text-yellow-600" />
                    {data?.profileFitmentScore || 0}/10
                  </div>
                </div>
                <hr className="border-b border-[#F3F4F6] my-2" />

                {Array.isArray(data?.profileFitmentStrengths) &&
                data?.profileFitmentStrengths.filter((v: any) => v).length > 0 ? (
                  <ul className="mt-4 space-y-3">
                    {data?.profileFitmentStrengths?.map((item: string, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2" />
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </CardContent>
            </Card>

            <Card className="bg-white shadow-sm border border-gray-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-medium flex items-center gap-2 text-gray-900">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  Position Specific Remarks
                </h3>
                <hr className="border-b border-[#F3F4F6] my-2" />

                {Array.isArray(data?.positionSpecificRemarks) &&
                data?.positionSpecificRemarks.filter((v: any) => v).length > 0 ? (
                  <ul className="mt-4 space-y-3">
                    {data?.positionSpecificRemarks?.map((item: string, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2" />
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </CardContent>
            </Card>
          </div>

          {data?.transcripts?.length > 0 ? (
            <>
              <div
                className={`grid grid-cols-1 gap-8 my-3 ${
                  data?.screeningQuestions?.generalQuestions?.length > 0 ||
                  data?.screeningQuestions?.logisticsQuestions?.length > 0
                    ? "lg:grid-cols-2"
                    : ""
                }`}
              >
                <Card className="bg-white shadow-sm border border-gray-200">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-medium flex items-center gap-2 text-gray-900">
                        <FileText className="h-5 w-5 text-blue-600" />
                        Overall Conclusion
                      </h3>
                    </div>
                    <hr className="border-b border-[#F3F4F6] my-2" />

                    <ul className="mt-4 space-y-3">
                      {data?.transcripts?.map((item: string, i: number) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
                {
                  (data?.screeningQuestions?.generalQuestions?.length > 0 ||
                    data?.screeningQuestions?.logisticsQuestions?.length > 0) && (
                    <SectionWiseScores
                      data={data}
                      evaluationType={interviewResult?.question?.evaluationType}
                      passThreshold={interviewResult?.question?.passThreshold}
                      sectionWeights={interviewResult?.question?.sectionWeights}
                    />
                  )}
              </div>
            </>
          ) : (
            (data?.screeningQuestions?.generalQuestions?.length > 0 ||
              data?.screeningQuestions?.logisticsQuestions?.length > 0) && (
              <div className="my-3">
                <SectionWiseScores
                  data={data}
                  evaluationType={interviewResult?.question?.evaluationType}
                  passThreshold={interviewResult?.question?.passThreshold}
                  sectionWeights={interviewResult?.question?.sectionWeights}
                />
              </div>
            )
          )}

          {/* Transcript Section */}
          {(data?.screeningQuestions?.generalQuestions?.length > 0 ||
            data?.screeningQuestions?.logisticsQuestions?.length > 0) && (
            <>
              <Card className="border border-gray-200 shadow-sm my-3 bg-[#EEF2FF]">
                <div className="text-center pt-3">
                  <h2 className="text-xl font-semibold text-cyan-500">Screening Questions</h2>
                  <div className="w-98 h-0.5 bg-cyan-500 mx-auto mt-2" />
                </div>
              </Card>
              <Card className="border-0 shadow-sm mb-3 rounded-[12px] bg-[#F7F9FA]">
                <div className="space-y-4 mb-3 bg-[#EFF6FF] p-3 rounded-t-[12px] border border-[#E5E7EB]">
                  <h3 className="text-lg font-medium text-gray-900">
                    Section-wise Questions & Answers
                  </h3>
                  <p className="text-sm text-grayCustom-400">
                    Candidate Questions & Answers overview
                  </p>
                </div>
                {data?.version <= 2 && (
                  <>
                    {data?.screeningQuestions?.generalQuestions?.length > 0 && (
                      <Card className="bg-white shadow-sm border border-gray-200 mb-3">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-center">
                            <h3 className="text-sm font-medium flex items-center gap-2 text-gray-900">
                              Candidate Profile & Fit Assessment:
                            </h3>
                          </div>

                          {data?.screeningQuestions?.generalQuestions?.map(
                            (item: any, i: number) => (
                              <Card className="mt-4 mb-4 bg-white shadow-sm border border-gray-200">
                                <CardContent className="p-0">
                                  <div className="flex-1 rounded-2xl p-4">
                                    <div className="text-sm font-medium text-foreground mb-1">
                                      <span className="px-[10px] py-[5px] rounded-full border-0 bg-blue-50">
                                        {i + 1}
                                      </span>{" "}
                                      <span className="text-[16px]">{item.question}</span>
                                    </div>

                                    <div className="text-[14px] leading-[25px] text-[#09090B]">
                                      <span className="font-bold mt-1">
                                        Transcript: <br />
                                      </span>
                                      <div className="p-2 text-sm text-muted-foreground leading-relaxed rounded-md bg-[#F7F9FA]">
                                        {item.candidate_answer}
                                      </div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            )
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {data?.screeningQuestions?.logisticsQuestions?.length > 0 && (
                      <Card className="bg-white shadow-sm border border-gray-200">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-center">
                            <h3 className="text-sm font-medium flex items-center gap-2 text-gray-900">
                              Requirements & Eligibility:
                            </h3>
                          </div>

                          {data?.screeningQuestions?.logisticsQuestions?.map(
                            (item: any, i: number) => (
                              <Card className="mt-4 mb-4 bg-white shadow-sm border border-gray-200">
                                <CardContent className="p-0">
                                  <div className="flex-1 rounded-2xl p-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1 text-sm font-medium text-foreground">
                                      <div className="flex items-center gap-2">
                                        <span className="px-[10px] py-[5px] rounded-full bg-blue-50">
                                          {i + 1}
                                        </span>
                                        <span className="text-[16px]">{item.question}</span>
                                      </div>

                                      {/* Score badge */}
                                      {(() => {
                                        const score = item?.score || 0;
                                        const percentage = score * 10;

                                        let color = "red";
                                        if (score > 4) color = "orange";
                                        if (score >= 7) color = "green";

                                        return (
                                          <div
                                            className={`inline-flex items-center gap-2 rounded-full border px-2 py-1 text-sm font-semibold text-${color}-600 bg-${color}-50 border-${color}-600 justify-center min-w-[150px] max-w-[150px] self-end sm:self-auto`}
                                          >
                                            <CheckCircle className="h-4 w-4" />
                                            <span>{percentage}% Matched</span>
                                          </div>
                                        );
                                      })()}
                                    </div>

                                    {item.expected_answer && (
                                      <>
                                        <div className="text-[14px] leading-[25px] text-[#09090B]">
                                          <span className="font-bold mt-1">
                                            Expected Answer: <br />
                                          </span>
                                          <div className="p-2 text-sm text-muted-foreground leading-relaxed rounded-md bg-[#EBF4FF]">
                                            {item.expected_answer}
                                          </div>
                                        </div>
                                        <hr className="mt-2 mb-2" />
                                      </>
                                    )}

                                    <div className="text-[14px] leading-[25px] text-[#09090B]">
                                      <span className="font-bold mt-1">
                                        Transcript: <br />
                                      </span>
                                      <div className="p-2 text-sm text-muted-foreground leading-relaxed rounded-md bg-[#F7F9FA]">
                                        {item.candidate_answer}
                                      </div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            )
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
                {data?.version >= 3 && (
                  <>
                    {data?.screeningQuestions?.logisticsQuestions?.length > 0 && (
                      <Card className="bg-white shadow-sm border border-gray-200 mb-3">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-center">
                            <h3 className="text-sm font-bold flex items-center gap-2 text-gray-900">
                              Candidate Profile & Fit Assessment:
                            </h3>
                          </div>

                          {data?.screeningQuestions?.logisticsQuestions?.map(
                            (item: any, i: number) => (
                              <Card className="mt-4 mb-4 bg-white shadow-sm border border-gray-200">
                                <CardContent className="p-0">
                                  <div className="flex-1 rounded-2xl p-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1 text-sm font-medium text-foreground">
                                      {/* Left side (Question + index) */}
                                      <div className="flex items-center gap-2">
                                        <span className="px-[10px] py-[5px] rounded-full bg-blue-50">
                                          {i + 1}
                                        </span>
                                        <span className="text-[16px]">
                                          {item.question}
                                          {item?.criteria === "mandatory" && (
                                            <span className="text-red-500 ml-1">*</span>
                                          )}
                                        </span>
                                      </div>

                                      {/* Right side (Score badge) */}
                                      {(() => {
                                        const status = item?.verdict || 0;
                                        let color = "red";
                                        if (status === "pass") color = "green";
                                        let statusMessage = status === "pass" ? "Passed" : "Failed";

                                        return (
                                          <div
                                            className={`inline-flex items-center gap-2 rounded-full border px-2 py-1 text-sm font-semibold text-${color}-600 bg-${color}-50 border-${color}-600 justify-center min-w-[100px] max-w-[150px] self-end sm:self-auto`}
                                          >
                                            {status === "pass" ? (
                                              <CheckCircle className="h-4 w-4" />
                                            ) : (
                                              <CircleX className="h-4 w-4" />
                                            )}
                                            <span>{statusMessage}</span>
                                          </div>
                                        );
                                      })()}
                                    </div>

                                    {item.expected_answer && (
                                      <>
                                        <div className="text-[14px] leading-[25px] text-[#09090B]">
                                          <span className="font-bold mt-1">
                                            Expected Answer: <br />
                                          </span>
                                          <div className="p-2 text-sm text-muted-foreground leading-relaxed rounded-md bg-[#EBF4FF]">
                                            {item.expected_answer}
                                          </div>
                                        </div>
                                        <hr className="mt-2 mb-2" />
                                      </>
                                    )}

                                    {item.explanation && (
                                      <div className="text-[14px] leading-[25px] text-[#09090B] border-t border-b border-gray-200 border-gray-200 py-2 pb-4 my-2">
                                        <span className="font-bold mt-1">
                                          Response Analysis: <br />
                                        </span>
                                        <div className="p-2 text-sm text-muted-foreground leading-relaxed rounded-md bg-[#EBF4FF] border-l-4 border-[#133F7D]">
                                          {item.explanation}
                                        </div>
                                      </div>
                                    )}

                                    <div className="text-[14px] leading-[25px] text-[#09090B]">
                                      <span className="font-bold mt-1">
                                        Transcript: <br />
                                      </span>
                                      <div className="p-2 text-sm text-muted-foreground leading-relaxed rounded-md bg-[#F7F9FA]">
                                        {item.candidate_answer}
                                      </div>
                                    </div>

                                    {/* Dynamic Follow ups: no Response Analysis —
                                        these questions are verdict-based, not scored */}
                                    <FollowUpConversation
                                      followUps={item?.follow_up_questions}
                                      showAiAnalysis={false}
                                    />
                                  </div>
                                </CardContent>
                              </Card>
                            )
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {data?.screeningQuestions?.generalQuestions?.length > 0 && (
                      <Card className="bg-white shadow-sm border border-gray-200">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-center">
                            <h3 className="text-sm font-bold flex items-center gap-2 text-gray-900">
                              Position Specific:
                            </h3>
                          </div>

                          {data?.screeningQuestions?.generalQuestions?.map(
                            (item: any, i: number) => (
                              <Card className="mt-4 mb-4 bg-white shadow-sm border border-gray-200">
                                <CardContent className="p-0">
                                  <div className="flex-1 rounded-2xl p-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1 text-sm font-medium text-foreground">
                                      <div className="flex items-center gap-2">
                                        <span className="px-[10px] py-[5px] rounded-full bg-blue-50">
                                          {i + 1}
                                        </span>
                                        <span className="text-[16px]">{item.question}</span>
                                      </div>

                                      {/* Right side (Score badge) */}
                                      {(() => {
                                        const score = item?.score || 0;
                                        const percentage = score * 10;

                                        let color = "red";
                                        if (score > 4) color = "orange";
                                        if (score >= 7) color = "green";

                                        return (
                                          <div
                                            className={`inline-flex items-center gap-2 rounded-full border px-2 py-1 text-sm font-semibold text-${color}-600 bg-${color}-50 border-${color}-600 justify-center min-w-[150px] max-w-[150px] self-end sm:self-auto`}
                                          >
                                            <CheckCircle className="h-4 w-4" />
                                            <span>{percentage}% Matched</span>
                                          </div>
                                        );
                                      })()}
                                    </div>

                                    {item.expected_answer && (
                                      <>
                                        <div className="text-[14px] leading-[25px] text-[#09090B]">
                                          <span className="font-bold mt-1">
                                            Expected Answer: <br />
                                          </span>
                                          <div className="p-2 text-sm text-muted-foreground leading-relaxed rounded-md bg-[#EBF4FF]">
                                            {item.expected_answer}
                                          </div>
                                        </div>
                                        <hr className="mt-2 mb-2" />
                                      </>
                                    )}

                                    <div className="text-[14px] leading-[25px] text-[#09090B]">
                                      <span className="font-bold mt-1">
                                        Transcript: <br />
                                      </span>
                                      <div className="p-2 text-sm text-muted-foreground leading-relaxed rounded-md bg-[#F7F9FA]">
                                        {item.candidate_answer}
                                      </div>
                                    </div>

                                    {item.explanation && (
                                      <div className="text-[14px] leading-[25px] text-[#09090B] py-2 pb-0">
                                        <span className="font-bold">
                                          Response Analysis: <br />
                                        </span>
                                        <div className="p-2 text-sm text-muted-foreground leading-relaxed rounded-md bg-[#EBF4FF] border-l-4 border-[#133F7D]">
                                          {item.explanation}
                                        </div>
                                      </div>
                                    )}

                                    {/* Dynamic Follow ups: */}
                                    <FollowUpConversation
                                      followUps={item?.follow_up_questions}
                                    />
                                  </div>
                                </CardContent>
                              </Card>
                            )
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </Card>
            </>
          )}

          <Card className="border border-gray-200 shadow-sm my-3 bg-[#EEF2FF]">
            <div className="text-center pt-3">
              <h2 className="text-xl font-semibold text-cyan-500">Transcript</h2>
              <div className="w-98 h-0.5 bg-cyan-500 mx-auto mt-2" />
            </div>
          </Card>

          <Card className="border border-gray-200 shadow-sm mb-3 rounded-[12px] bg-[#F7F9FA]">
            <Card className="bg-white m-3">
              <CardContent className="p-3 space-y-6">
                <div className="text-lg font-medium text-gray-900 mb-4">Conversation:</div>
                {/* add single conversation as well */}
                {data?.questions
                  // ?.filter((q: QuestionItem) => q.answer !== "") // Filter out unanswered questions
                  ?.map((q: QuestionItem, i: number) => (
                    <ConversationItem
                      key={i}
                      question={q.question}
                      answer={q.answer ?? null}
                      isLast={i === data.questions.length - 1}
                      name={profileForm.name}
                    />
                  ))}
              </CardContent>
            </Card>
          </Card>

          {/* Call Events Log */}
          {data?.logs &&
            data.logs.length > 0 &&
            (() => {
              // [2026-10-08] CHANGED — the list of events now lives in utils/callEventsLog.ts and also
              // has the scheduling event ("Slot Updated": a callback booked on the AI call, or a slot
              // picked on the slot page). Old code kept for grep/compare/revert:
              // const ALLOWED_ACTIONS = new Set([
              //   "Call Initiated",
              //   "AI ended the call",
              //   "Candidate ended the call",
              //   "Call reached the candidate's voicemail",
              //   "Candidate's line was busy",
              //   "Call ended due to prolonged silence",
              //   "Candidate did not answer the call",
              //   "Completed",
              //   "Call Connection Failed",
              // ]);
              //
              // const filteredLogs = data.logs.filter((log: any) => ALLOWED_ACTIONS.has(log.action));
              const filteredLogs = callEventsForReport(data.logs);
              if (filteredLogs.length === 0) return null;

              const getDotColor = (action: string) => {
                if (action === "Completed") return "bg-green-500";
                return "bg-blue-500";
              };
              const isCompleted = (action: string) => action === "Completed";

              const grouped = filteredLogs.reduce((acc: Record<string, any[]>, log: any) => {
                const d = new Date(log.createdAt);
                const key = d.toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                });
                if (!acc[key]) acc[key] = [];
                acc[key].push(log);
                return acc;
              }, {});

              return (
                <Card className="border border-gray-200 shadow-sm mb-3 rounded-[12px] bg-[#F7F9FA]">
                  <Card className="bg-white m-3">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-3 pb-1">
                        <img src={callEventsLog} alt="call events" className="w-10 h-10" />
                        <span className="text-lg font-semibold text-gray-900">Call Events Log</span>
                      </div>
                      <hr className="border-gray-200" />
                      {Object.entries(grouped).map(([dateLabel, logs]) => (
                        <div key={dateLabel} className="space-y-1">
                          <div className="text-sm font-semibold text-gray-700 mb-2">
                            {dateLabel}
                          </div>
                          <div className="flex gap-0">
                            <div className="w-[3px] bg-blue-500 rounded-full mr-5 flex-shrink-0" />
                            <div className="space-y-4 flex-1">
                              {(logs as any[]).map((log: any, i: number) => {
                                const d = new Date(log.createdAt);
                                const timeStr = d.toLocaleTimeString("en-US", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                });
                                return (
                                  <div key={i} className="flex items-center gap-4">
                                    <span className="text-sm text-gray-500 w-20 flex-shrink-0">
                                      {timeStr}
                                    </span>
                                    <span
                                      className={`w-3 h-3 rounded-full flex-shrink-0 ${getDotColor(log.action)}`}
                                    />
                                    {/* [2026-10-08] CHANGED — the entry's detail line is printed under
                                        the event, e.g. "Wed 7 Oct, 6:55–7:55 PM — requested on AI call".
                                        An entry without a detail renders exactly as before. Old:
                                        <span className="text-sm font-medium text-gray-600">{log.action}</span> */}
                                    <span className="text-sm font-medium text-gray-600">
                                      {log.action}
                                      {log.detail && (
                                        <span className="block text-xs font-normal text-gray-500">
                                          {log.detail}
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </Card>
              );
            })()}
        </div>
      </div>
    </div>
  );
};

export default Feedback;
